import { chooseLoadingMessage, rememberLoadingMessage } from "./loading-message";
import { useBrandLogo } from "./branding-theme";
import { useBrandImage } from "./brand-cache";
import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from "react";

import { Button, Shimmer, Skeleton } from "@organizacaox/lai-design-system";
import { RotateCcw, TriangleAlert } from "@organizacaox/lai-design-system/icons";
import { useRouter, type ErrorComponentProps } from "@organizacaox/lai-design-system/router";

import type { Branding } from "@/contracts";
import { resolvePublicTenantHost } from "@/lib/api-base-url";

import { useAppLoadingData } from "@/lib/hooks/app-states";
import { useCachedBrand } from "@/lib/use-cached-brand";
import { resolvePwaBranding } from "@/pwa/branding";

const screenClassName =
  "fixed inset-0 z-50 flex min-h-svh flex-col items-center justify-center gap-5 overflow-y-auto bg-background px-6 py-10 text-center text-foreground";

type Brand = Pick<Branding, "logo" | "images" | "settings" | "texts" | "colors">;

function AppBrand({
  brand,
  pending = false,
  showName = true,
}: {
  brand?: Brand;
  pending?: boolean;
  showName?: boolean;
}) {
  const [failedLogo, setFailedLogo] = useState<string | null>(null);
  const identity = resolvePwaBranding(brand);
  const themedLogo = useBrandLogo(brand);
  const pwaIcon = useBrandImage(
    typeof brand?.images?.pwaIcon === "string" ? brand.images.pwaIcon : null,
  );
  const logo = pwaIcon ?? themedLogo ?? identity.icon;
  const name = identity.name;
  return (
    <div className="flex flex-col items-center gap-4">
      {pending ? (
        <Skeleton aria-hidden="true" className="h-20 w-40 motion-reduce:animate-none" />
      ) : logo && logo !== failedLogo ? (
        <img
          src={logo}
          alt=""
          className="h-20 w-40 object-contain"
          onError={() => setFailedLogo(logo)}
        />
      ) : (
        <img src="/logo-mark.svg" alt="" className="size-20 object-contain" />
      )}
      {showName &&
        (pending ? (
          <Skeleton aria-hidden="true" className="h-5 w-32 motion-reduce:animate-none" />
        ) : (
          <p className="text-lg font-semibold tracking-tight">{name}</p>
        ))}
    </div>
  );
}


// O fallback pode aparecer antes de qualquer provider ou sessão estar pronto.
// A consulta pública usa o mesmo cache do login e nunca bloqueia a navegação.
export function AppLoadingScreen() {
  const [message] = useState(chooseLoadingMessage);
  useEffect(() => rememberLoadingMessage(message.index), [message.index]);
  const host = resolvePublicTenantHost();
  const brand = useCachedBrand();
  const { data, isPending } = useAppLoadingData({
    host,
    brand,
  });
  // O fundo segue o tema escolhido (claro/escuro), já aplicado no index.html
  // antes do primeiro paint; a cor de fundo do PWA fica só no splash nativo.
  return (
    <div className={screenClassName}>
      <AppBrand brand={brand ?? data} pending={!brand && isPending} showName={false} />
      <div role="status" aria-live="polite" aria-busy="true">
        <Shimmer duration={1800} className="text-sm text-current opacity-70">
          {message.text}
        </Shimmer>
      </div>
    </div>
  );
}

export function AppErrorScreen({ onRetry }: { onRetry?: () => void | Promise<void> }) {
  const [retrying, setRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);
  async function retry() {
    if (!onRetry || retrying) return;
    setRetrying(true);
    setRetryFailed(false);
    try {
      await onRetry();
    } catch {
      setRetryFailed(true);
    } finally {
      setRetrying(false);
    }
  }
  return (
    <div className={screenClassName}>
      <AppBrand brand={useCachedBrand()} />
      <div role="alert" className="flex max-w-md flex-col items-center gap-3">
        <TriangleAlert className="size-7 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-xl font-semibold">Não foi possível carregar esta página.</h1>
        <p className="text-sm text-muted-foreground">
          Confira sua conexão e tente novamente. Se o problema continuar, recarregue a página.
        </p>
        {retryFailed ? (
          <p className="text-sm text-destructive">
            A tentativa falhou. Recarregue a página para tentar de novo.
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap justify-center gap-3">
        {onRetry ? (
          <Button disabled={retrying} onClick={() => void retry()}>
            <RotateCcw aria-hidden="true" />
            {retrying ? "Tentando novamente…" : "Tentar novamente"}
          </Button>
        ) : null}
        <Button variant={onRetry ? "outline" : "default"} onClick={() => window.location.reload()}>
          Recarregar página
        </Button>
      </div>
    </div>
  );
}

export function RouteErrorScreen({ reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <AppErrorScreen
      onRetry={async () => {
        await router.invalidate();
        reset();
      }}
    />
  );
}

// Última barreira, fora do roteador e dos providers. Não depende deles para exibir o erro.
export class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("education.app_error", error, info.componentStack);
  }

  override render() {
    return this.state.failed ? (
      <AppErrorScreen onRetry={() => this.setState({ failed: false })} />
    ) : (
      this.props.children
    );
  }
}
